import {uniq} from 'lodash';

import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {isSidePanelIntegration} from '../../../helpers/integrationHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {
  ApiIntegration,
  ApiIntegrations,
  IntegrationModel,
  IntegrationSettingsTypesEnum,
} from '../../../models/api/integrationModel';
import {IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {openElectronAuthPopup} from '../../../services/electron/electronBridgeOutbound';
import {
  deleteAsync,
  fetchApiAsync,
  fetchAsync,
  postApiAsync,
  postAsync,
  putApiAsync,
} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {openRelativePopupUrl} from '../../../services/popups';
import {batchStore} from '../../appBatching';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getHasNewIntegrationsDiscoverability} from '../../featureFlags/featureFlagsSelectors';
import {showApplicationEnabledBannerAction} from '../../panels/app/banners/bannersActions';
import {getOptionalFirstAppPanelId} from '../../panels/panelsSelectors';
import {AppPanelId} from '../../panels/panelStateBase';
import {getCompanyUrl} from '../company/companySelectors';
import {getFlatIntegrations, getIntegrations, getIntegrationsByType} from '../dataSelectors';
import {updateEnabledIntegrationIdsAction} from '../localPreferences/localPreferencesActions';
import {getLocalPreferences} from '../localPreferences/localPreferencesSelectors';
import {getIsUserGlobalAdmin} from '../teammates/teammatesSelectors';

/*
 * Interfaces.
 */

interface BuildIntegrationSignOutUrlResponse {
  url: string;
  method: 'DELETE' | 'POST';
}

/*
 * Action types.
 */

export enum IntegrationsActionsEnum {
  INTEGRATION_UPDATED = '@@data/integrations/INTEGRATION_UPDATED',
  INTEGRATION_DELETED = '@@data/integrations/INTEGRATION_DELETED',
}

/*
 * Types.
 */

export interface IntegrationsUpdatedAction extends ActionWithPayload<IntegrationModel> {
  type: IntegrationsActionsEnum.INTEGRATION_UPDATED;
}

export interface IntegrationsDeletedAction extends ActionWithPayload<number> {
  type: IntegrationsActionsEnum.INTEGRATION_DELETED;
}

export interface CreateUpdateIntegrationAsyncActionPayload {
  isEnabled?: boolean;
  settings?: object;
}

/*
 * Action creators.
 */

export function fetchAllIntegrationsAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const response = await fetchApiAsync(ApiIntegrations, `${companyUrl}/integrations`);
      batch(() => {
        response.forEach((integration) => dispatch(updateIntegrationAction(integration)));
      });
    } catch (err) {
      // TODO: Maybe handle errors when trying to list.
    }
  };
}

export function decryptIntegrationSettingsAsyncAction(integration: IntegrationModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const isGlobalAdmin = getIsUserGlobalAdmin(getState());

    // Only global admins can decrypt settings.
    if (!isGlobalAdmin) {
      return;
    }

    try {
      const decryptedSettings = await fetchAsync(
        `${companyUrl}/integrations/${integration.id}/decrypted_settings`,
      );
      dispatch(
        updateIntegrationAction({
          ...integration,
          settings: decryptedSettings,
        }),
      );
    } catch (err) {
      // TODO: Maybe handle errors when trying to decrypt.
    }
  };
}

export function createIntegrationAsyncAction(
  integrationType: IntegrationTypeModel,
  payload: CreateUpdateIntegrationAsyncActionPayload,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Check to see if we already have an integration, if we do return early.
    const integration = getIntegrationsByType(getState())[integrationType.id];
    if (integration) {
      return;
    }

    const companyUrl = getCompanyUrl(getState());
    try {
      const response = await postApiAsync(ApiIntegration, `${companyUrl}/integrations`, {
        type: integrationType,
        enabled: payload.isEnabled ?? true,
        settings: payload.settings || null,
      });
      dispatch(updateIntegrationAction(response));
    } catch (err) {
      // TODO: potentially handle the error state when we fail to create.
    }
  };
}

export function updateIntegrationAsyncAction(
  integrationId: number,
  payload: CreateUpdateIntegrationAsyncActionPayload,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const integration = getIntegrations(getState())[integrationId];

    // Make sure we have a integration that can be updated.
    if (!integration) {
      return;
    }

    const settings = payload.settings ?? integration.settings;
    const isEnabled = payload.isEnabled ?? integration.isEnabled;

    try {
      const response = await putApiAsync(ApiIntegration, `${companyUrl}/integrations/${integration.id}`, {
        type: {
          id: `${integrationId}`,
        },
        enabled: isEnabled,
        // If the app is not enabled, we do not want to update any settings.
        settings: isEnabled ? settings : undefined,
      });
      dispatch(updateIntegrationAction(response));
    } catch (err) {
      // TODO: potentially handle the error state when we fail to update.
    }
  };
}

export function updateIntegrationAction(newIntegration: IntegrationModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing integration.
    const existingIntegration = getState().data.integrations[newIntegration.id];

    // No need to do anything if the new integration is older.
    if (existingIntegration && existingIntegration.updatedAt > newIntegration.updatedAt) {
      return;
    }

    if (
      !existingIntegration?.isEnabled &&
      newIntegration.isEnabled &&
      isSidePanelIntegration(newIntegration) &&
      getHasNewIntegrationsDiscoverability(getState())
    ) {
      const appPanelId = getOptionalFirstAppPanelId(getState());

      if (appPanelId) {
        dispatch(showApplicationEnabledBannerAction(appPanelId, newIntegration));
      }
    }

    // Patch the existing with the new.
    const action: IntegrationsUpdatedAction = {
      type: IntegrationsActionsEnum.INTEGRATION_UPDATED,
      payload: {
        ...existingIntegration,
        ...newIntegration,
        // Teammate settings should not be overwritten if the new integration value is set to undefined
        teammateSettings: newIntegration.teammateSettings ?? existingIntegration?.teammateSettings ?? {},
      },
    };

    // Get all integrations and track the newly enabled integration ids and store it in local prefs.
    const integrations = getFlatIntegrations(getState());
    const enabledIntegrationIds = uniq(
      [...integrations, newIntegration].filter((a) => a.isEnabled).map((a) => a.id),
    );
    batchStore(() => {
      dispatch(action);
      dispatch(updateEnabledIntegrationIdsAction(enabledIntegrationIds));
    });
  };
}

export function updateIntegrationTeammateSettingsAction(
  integrationId: number,
  newIntegrationTeammateSettings: object,
): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this integration, nothing to do.
    const existingIntegration = getState().data.integrations[integrationId];
    if (!existingIntegration) {
      return;
    }

    // Patch with the new settings.
    const action: IntegrationsUpdatedAction = {
      type: IntegrationsActionsEnum.INTEGRATION_UPDATED,
      payload: {...existingIntegration, teammateSettings: newIntegrationTeammateSettings},
    };
    dispatch(action);
  };
}

export function deleteIntegrationAction(integrationId: number): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this integration, nothing to do.
    const existingIntegration = getState().data.integrations[integrationId];
    if (!existingIntegration) {
      return;
    }

    const action: IntegrationsDeletedAction = {
      type: IntegrationsActionsEnum.INTEGRATION_DELETED,
      payload: integrationId,
    };
    dispatch(action);
  };
}

/*
 * Actions.
 */

export function signInToCompanySalesforceAccountAction(shouldUseTestEnv?: boolean): ThunkAction {
  return (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const signInUrl = `${companyUrl}/integration_types/${
      IntegrationSettingsTypesEnum.SALESFORCE
    }/oauth/login?state_data=company${shouldUseTestEnv ? '_test' : ''}`;

    if (openElectronAuthPopup(signInUrl)) {
      return;
    }
    openRelativePopupUrl(signInUrl, {title: 'Salesforce'});
  };
}

export function signInToIntegrationAccountAction(
  integrationType: IntegrationSettingsTypesEnum,
  title: string,
  shouldUseTestEnv?: boolean,
  customStateData?: string,
): ThunkAction {
  return (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const signInUrlComputed = buildIntegrationSettingsSignInUrl(companyUrl, integrationType, customStateData);
    const signInUrl = `${signInUrlComputed}${shouldUseTestEnv ? '_test' : ''}`;

    if (openElectronAuthPopup(signInUrl)) {
      return;
    }
    openRelativePopupUrl(signInUrl, {title});
  };
}

export function signInToJiraSelfHostedIntegrationAccountAction(
  integrationType: IntegrationSettingsTypesEnum,
  title: string,
): ThunkAction {
  return (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const signInUrl = `${companyUrl}/integration_types/${integrationType}/oauth/1/login?state=company`;

    if (openElectronAuthPopup(signInUrl)) {
      return;
    }
    openRelativePopupUrl(signInUrl, {title});
  };
}

export function signOutOfIntegrationAsyncAction(
  integrationType?: IntegrationSettingsTypesEnum,
): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    if (!integrationType) {
      return;
    }
    const companyUrl = getCompanyUrl(getState());
    const signOutInfo = buildIntegrationSettingsSignOutUrl(companyUrl, integrationType);

    if (!signOutInfo) {
      return;
    }

    try {
      if (signOutInfo.method === 'DELETE') {
        await deleteAsync(signOutInfo.url);
      } else if (signOutInfo.method === 'POST') {
        await postAsync(signOutInfo.url);
      }
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function generateConfigurationKeysForIntegrationAsyncAction(
  integrationType: IntegrationSettingsTypesEnum,
): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    try {
      const companyUrl = getCompanyUrl(getState());
      await postAsync(`${companyUrl}/integration_types/${integrationType}/oauth/1/configkeys`);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function revokeConfigurationKeysForIntegrationAsyncAction(
  integrationType: IntegrationSettingsTypesEnum,
): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    try {
      const companyUrl = getCompanyUrl(getState());
      await deleteAsync(`${companyUrl}/integration_types/${integrationType}/oauth/1/revokekeys`);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function updateEnabledIntegrationsAndDisplayBannersAction(panelId: AppPanelId): ThunkAction {
  return (dispatch, getState) => {
    const integrations = getFlatIntegrations(getState());
    const storedEnabledIntegrationIds = getLocalPreferences(getState()).enabledIntegrationIds;
    const hasNewIntegrationsDiscoverability = getHasNewIntegrationsDiscoverability(getState());

    // Filter only enabled integrations.
    const enabledIntegrations = [...integrations].filter((i) => i.isEnabled);

    // If we have no stored integration ids we can assume this is coming from a clean boot.
    // When we have a clean boot, we can't show the banners since it could show integrations
    // that have already been enabled.
    // TODO: Also if they are not on the feature flag we can short-circut this. Remove when feature flag shipped.
    if (storedEnabledIntegrationIds.length === 0 || !hasNewIntegrationsDiscoverability) {
      dispatch(updateEnabledIntegrationIdsAction([...enabledIntegrations].map((i) => i.id)));
      return;
    }

    batchStore(() => {
      // Loop through all the currently enabled integrations.
      for (const integration of enabledIntegrations) {
        // If the integration is already enabled, we do not need to do anything.
        if (storedEnabledIntegrationIds.includes(integration.id)) {
          continue;
        }
        // If the integration doesn't have a sidepanel option, we do not need to show
        // the banner at all.
        if (isSidePanelIntegration(integration)) {
          continue;
        }

        dispatch(showApplicationEnabledBannerAction(panelId, integration));
      }
      // Update the enabled integration ids.
      dispatch(updateEnabledIntegrationIdsAction([...enabledIntegrations].map((i) => i.id)));
    });
  };
}

export function fetchApiSecretAsyncAction(): ThunkAsyncAction<string> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const response = await fetchAsync(`${companyUrl}/api_secret`);

      return response.api_secret;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/*
 * Helpers.
 */

function buildIntegrationSettingsSignInUrl(
  companyUrl: string,
  integrationType: IntegrationSettingsTypesEnum,
  customStateData?: string,
): string {
  switch (integrationType) {
    case IntegrationSettingsTypesEnum.JIRA:
      return `${companyUrl}/integration_types/${integrationType}/auth/form?state=company`;
    default:
      return `${companyUrl}/integration_types/${integrationType}/oauth/login?state_data=${
        customStateData || 'company'
      }`;
  }
}

function buildIntegrationSettingsSignOutUrl(
  companyUrl: string,
  integrationType: IntegrationSettingsTypesEnum,
): BuildIntegrationSignOutUrlResponse | undefined {
  switch (integrationType) {
    case IntegrationSettingsTypesEnum.HUBSPOT:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/auth/company/signout`,
        method: 'DELETE',
      };
    case IntegrationSettingsTypesEnum.SALESFORCE:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/oauth/company/signout`,
        method: 'POST',
      };
    case IntegrationSettingsTypesEnum.ASANA:
    case IntegrationSettingsTypesEnum.GITHUB:
    case IntegrationSettingsTypesEnum.INTERCOM:
    case IntegrationSettingsTypesEnum.PIPEDRIVE:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/oauth/signout?state_data=company`,
        method: 'DELETE',
      };

    case IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/oauth/company/signout`,
        method: 'DELETE',
      };
    case IntegrationSettingsTypesEnum.JIRA:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/signout?state_data=company`,
        method: 'DELETE',
      };
    case IntegrationSettingsTypesEnum.PAGERDUTY:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/auth/signout?state_data=company`,
        method: 'DELETE',
      };
    case IntegrationSettingsTypesEnum.SLACK:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/oauth/company/signout`,
        method: 'DELETE',
      };
    case IntegrationSettingsTypesEnum.TRELLO:
      return {
        url: `${companyUrl}/integration_types/${integrationType}/auth/signout?state_data=company`,
        method: 'DELETE',
      };
    default:
      return undefined;
  }
}

/*
 * Union type.
 */

export type IntegrationsAction = IntegrationsUpdatedAction | IntegrationsDeletedAction;
